/**
 * Stable-error-code extraction, kept in its own module on purpose.
 *
 * `@/lib/error` imports `getStatusCode` from `@/lib/axios`, so the axios layer
 * cannot import back from it — and the axios response interceptor needs this
 * exact reading of the code (QTY-2042's mid-session lockout). Both sides import
 * this leaf module instead of the regex being copied into two places and
 * drifting apart. `@/lib/error` re-exports it, so existing call sites are
 * unchanged.
 */

/**
 * A bare stable code the backend returns in `message`, e.g. `STAFF_INACTIVE`.
 * Module-scope so it isn't rebuilt per call, and non-global so it carries no
 * `lastIndex` state between calls.
 */
const STABLE_CODE_PATTERN = /^[A-Z][A-Z0-9_]{3,}$/

/**
 * Extract a stable error code if present.
 *
 * Reads, in order: a dedicated `errorCode` field (future backend contract),
 * `data.code` and `errors[0].code` (shapes already in use), then a bare
 * UPPER_SNAKE token returned in the `message` field — which is what the
 * backend actually sends today for policy verdicts.
 */
export function extractErrorCode(error: any): string | null {
  const explicit: unknown =
    error?.response?.data?.errorCode ??
    error?.response?.data?.code ??
    error?.response?.errors?.[0]?.code
  if (typeof explicit === 'string' && explicit) return explicit

  const message: unknown = error?.response?.data?.message ?? error?.response?.errors?.[0]?.message
  if (typeof message === 'string' && STABLE_CODE_PATTERN.test(message.trim())) {
    return message.trim()
  }
  return null
}
